// Floating AI Tournament Assistant — dark-green bubble, slide-up chat
// panel. Renders on both the admin dashboard (tournamentId=null) and
// inside live tournaments. CREATE/COPY/SETUP_COMPLETE_TOURNAMENT work
// without a tournamentId because they CREATE the tournament themselves;
// per-tournament actions (MOVE_PLAYER, ADD_PLAYER, etc.) fail loudly via
// the executor when no tournamentId is set, which is fine — the AI is
// the surface, not the gate.
// All Anthropic calls go through the Firebase callable in functions/index.js.

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { db } from '../lib/firebase.js'
import { FOURBALL_ONLY, PBCC_PREMIUM } from '../config/siteMode.js'
import { askAI, loadTournamentContext } from '../utils/aiAssistant.js'
import { executeAIAction, buildDemoTournament } from '../utils/aiActionExecutor.js'
import { useAiAssistantHidden } from '../lib/aiAssistantHide.js'

const EXAMPLES = [
  'Create a 96 team 4-ball for July 12th',
  'Delay all tee times 20 minutes',
  'Mark Tom Jones as no-show',
  'Build flights based on handicap',
  'Notify players we’re delayed 30 minutes',
  'Finalize results',
  'Open Calcutta bidding',
]

// Read-only lookups that skip the do-it/cancel gate — the executor reads
// club data and returns the answer straight into the chat.
const LOOKUP_ACTIONS = new Set(['PLAYER_HANDICAP_LOOKUP', 'COURSE_LOOKUP', 'GHIN_LOOKUP', 'HANDICAP_REVIEW'])

export default function AIAssistant({ tournamentId, demoMode = null, demoSize = 96 }) {
  // Hidden while a page/tab that owns its own AI (e.g. the Calcutta tab, whose
  // auction AI is built in) is mounted. Kept everywhere else.
  const aiHidden = useAiAssistantHidden()
  const [open, setOpen] = useState(false)
  // messages: [{ role:'user'|'ai'|'system', text, pending? , action? , params? , confirmation? , error? }]
  const [messages, setMessages] = useState([])

  // Window event from the QualifyingTab "Build with AI" button — pre-
  // fills the input with a command and opens the panel so the director
  // just has to hit Send.
  useEffect(() => {
    const handler = (ev) => {
      const cmd = ev?.detail?.command
      if (typeof cmd === 'string' && cmd.length > 0) setInput(cmd)
      if (ev?.detail?.autoSend && typeof cmd === 'string' && cmd.length > 0) {
        // "Set Up with AI" — build silently, do NOT pop the chat panel open.
        setTimeout(() => send(cmd, { autoConfirm: true }), 600)
        return
      }
      if (ev?.detail?.open !== false) setOpen(true)
    }
    window.addEventListener('ai-prefill', handler)
    return () => window.removeEventListener('ai-prefill', handler)
  }, [])

  // sessionStorage-based prefill — used by the QualifyingEvents "Set Up
  // with AI" flow because the ai-prefill window event raced the
  // tournament page's AIAssistant mount (event fired before this
  // component attached its listener). Reading here on mount avoids the
  // race entirely; we clear the slot so the prefill doesn't replay on
  // subsequent loads of the same tournament page.
  useEffect(() => {
    const stored = sessionStorage.getItem('ai-prefill')
    if (stored) {
      sessionStorage.removeItem('ai-prefill')
      const { command, autoSend } = JSON.parse(stored)
      if (command) {
        setInput(command)
        if (autoSend) {
          // "Set Up with AI" — build silently, don't open the chat panel.
          setTimeout(() => send(command, { autoConfirm: true }), 600)
        } else {
          setOpen(true)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (!open || !demoMode) return
    setMessages((m) => {
      if (m.some((x) => x.demoIntro)) return m
      const introText = demoMode === 'strokePlay'
        ? 'You are in Stroke Play Demo Mode. Tell me to create a stroke play ' +
          'tournament and I will build it from the saved 36-player template.'
        : 'You are in Demo Mode. Tell me to create a 4-ball tournament and ' +
          'I will build it from the saved PBCC template.'
      return [...m, { role: 'ai', text: introText, demoIntro: true }]
    })
  }, [open, demoMode])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const scrollRef = useRef(null)

  // Voice input — Web Speech API. Feature-detect at mount so we can hide
  // the mic button silently on browsers without support. recognitionRef
  // holds the live SpeechRecognition instance while listening, and
  // silenceTimerRef is the 5-second hard timeout that force-stops if the
  // user goes quiet so the panel never gets stuck "listening".
  const [voiceSupported, setVoiceSupported] = useState(false)
  const [listening, setListening] = useState(false)
  const [voiceToast, setVoiceToast] = useState('')
  const recognitionRef = useRef(null)
  const silenceTimerRef = useRef(null)
  useEffect(() => {
    const SR = typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null
    setVoiceSupported(!!SR)
  }, [])

  // Auto-scroll on new message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, open])

  // Detect mobile breakpoint for the bottom-sheet layout.
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const lastTwenty = useMemo(() => messages.slice(-20), [messages])

  // ── Voice input handlers ─────────────────────────────────────────────
  function showVoiceToast(msg) {
    setVoiceToast(msg)
    window.setTimeout(() => setVoiceToast(''), 2000)
  }

  function stopListening() {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current)
      silenceTimerRef.current = null
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop() } catch {}
      recognitionRef.current = null
    }
    setListening(false)
  }

  function startListening() {
    if (busy || listening) return
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) return
    const rec = new SR()
    rec.continuous = false
    rec.interimResults = false
    rec.lang = 'en-US'

    rec.onresult = (event) => {
      const transcript = event?.results?.[0]?.[0]?.transcript || ''
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null }
      recognitionRef.current = null
      setListening(false)
      const trimmed = transcript.trim()
      if (!trimmed) {
        showVoiceToast("Couldn't hear that, try again")
        return
      }
      // Populate the input field for visual confirmation, then immediately
      // submit. We call send() with the literal transcript so we don't have
      // to wait for the input-state setter to flush before reading it back.
      setInput(trimmed)
      send(trimmed)
    }
    rec.onerror = (event) => {
      const code = event?.error || ''
      // 'no-speech' / 'aborted' come from our 5s hard-stop or the user
      // tapping the mic again — those aren't real errors worth toasting.
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null }
      recognitionRef.current = null
      setListening(false)
      if (code && code !== 'aborted' && code !== 'no-speech') {
        showVoiceToast("Couldn't hear that, try again")
      }
    }
    rec.onend = () => {
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null }
      recognitionRef.current = null
      setListening(false)
    }

    recognitionRef.current = rec
    setListening(true)
    try { rec.start() } catch {
      setListening(false)
      recognitionRef.current = null
      showVoiceToast("Couldn't hear that, try again")
      return
    }
    // 5-second hard timeout — if the user goes silent or the browser
    // forgets to fire `end`, we still drop out of the listening state.
    silenceTimerRef.current = window.setTimeout(() => {
      try { rec.stop() } catch {}
    }, 5000)
  }

  async function send(text, opts = {}) {
    const command = String(text || '').trim()
    if (!command || busy) return
    // autoConfirm: skip the Do-it/Cancel gate and run the action immediately.
    // Used by "Set Up with AI" (tab). Manual typing in the circle stays gated.
    const autoConfirm = !!opts.autoConfirm
    setInput('')
    setMessages((m) => [...m, { role: 'user', text: command }])
    setBusy(true)
    setMessages((m) => [...m, { role: 'ai', text: 'Thinking…', pending: true }])
    try {
      const ctx = await loadTournamentContext(db, tournamentId)
      const res = await askAI(command, ctx, { demoMode })
      if (res?.understood && !res.readOnly && autoConfirm) {
        // "Set Up with AI" — no confirmation, just build it.
        setMessages((m) => [...m.filter((x) => !x.pending), {
          role: 'ai',
          text: res.confirmation || 'Building the tournament…',
          command, action: res.action, params: res.params || {},
        }])
        await runAction({ action: res.action, params: res.params || {}, command })
        return
      }
      // Read-only club lookups (handicap / course questions) — run the
      // executor immediately and show the answer. No do-it/cancel gate:
      // these are pure reads, the executor composes the reply from club data.
      if (res?.understood && LOOKUP_ACTIONS.has(res.action)) {
        setMessages((m) => m.filter((x) => !x.pending))
        await runAction({ action: res.action, params: res.params || {}, command })
        return
      }
      setMessages((m) => {
        const trimmed = m.filter((x) => !x.pending)
        if (res?.understood) {
          // Read-only actions (e.g. CALCUTTA_STANDINGS, CALCULATE_PAYOUTS) put the
          // full human-readable answer inside `confirmation` and skip the
          // do-it/cancel flow — they render a Close-only message instead.
          if (res.readOnly) {
            return [...trimmed, {
              role: 'ai',
              text: res.confirmation || 'No data available.',
              command,
              action: res.action,
              params: res.params || {},
              readOnly: true,
              awaitingClose: true,
            }]
          }
          return [...trimmed, {
            role: 'ai',
            text: res.confirmation || 'Ready to proceed.',
            command,
            action: res.action,
            params: res.params || {},
            confirmation: res.confirmation || '',
            awaitingConfirm: true,
          }]
        }
        return [...trimmed, {
          role: 'ai',
          text: res?.error || "Sorry, I didn't understand that.",
          error: true,
        }]
      })
    } catch (err) {
      setMessages((m) => {
        const trimmed = m.filter((x) => !x.pending)
        return [...trimmed, {
          role: 'ai',
          text: `AI error: ${err?.message || String(err)}`,
          error: true,
        }]
      })
    } finally {
      setBusy(false)
    }
  }

  // Shared execution — used by the Do-it button (doConfirm) and by the
  // auto-confirm path ("Set Up with AI" tab). Runs the action and appends the
  // result message. Does NOT manage `busy`; the caller does.
  async function runAction({ action, params = {}, command = '' }) {
    // Demo-mode hard lock — bypass whatever the model returned and build
    // through the SAME shared builder the demo-launcher uses so tab and circle
    // produce the identical result.
    const CREATION_ACTIONS = new Set([
      'CREATE_TOURNAMENT', 'SETUP_COMPLETE_TOURNAMENT', 'BUILD_FLIGHTS', 'GENERATE_TEE_TIMES',
    ])
    const src = params?.sourceTournamentId
    const isSizeBlindCopy = action === 'COPY_TOURNAMENT' &&
      (!src || src === 'PBCC_TEMPLATE' || src === 'templates/demos/pbcc-4-ball')
    let result
    if (demoMode && (CREATION_ACTIONS.has(action) || isSizeBlindCopy)) {
      result = await buildDemoTournament(db, {
        mode: demoMode, size: demoSize,
        name: params.newName || params.name, date: params.newDate || params.date,
        tournamentId,
      })
    } else {
      result = await executeAIAction(action, params, tournamentId, db, command)
    }
    setMessages((m) => [...m, {
      role: 'ai',
      text: result.message || (result.success ? 'Done.' : 'Action failed.'),
      success: !!result.success,
      error: !result.success,
    }])
    return result
  }

  async function doConfirm(idx) {
    const msg = messages[idx]
    if (!msg || !msg.awaitingConfirm) return
    setBusy(true)
    setMessages((m) => m.map((x, i) => i === idx ? { ...x, awaitingConfirm: false, executing: true } : x))
    try {
      await runAction({ action: msg.action, params: msg.params || {}, command: msg.command || '' })
      setMessages((m) => m.map((x, i) => i === idx ? { ...x, executing: false, executed: true } : x))
    } catch (err) {
      setMessages((m) => [
        ...m.map((x, i) => i === idx ? { ...x, executing: false } : x),
        { role: 'ai', text: `Error: ${err?.message || String(err)}`, error: true },
      ])
    } finally {
      setBusy(false)
    }
  }

  function doCancel(idx) {
    setMessages((m) => [
      ...m.map((x, i) => i === idx ? { ...x, awaitingConfirm: false, cancelled: true } : x),
      { role: 'system', text: 'Cancelled.' },
    ])
  }

  // Read-only action — just dismiss the action prompt. Best-effort log to
  // aiLog so the audit trail still captures that the director viewed it.
  async function doClose(idx) {
    const msg = messages[idx]
    setMessages((m) => m.map((x, i) => i === idx ? { ...x, awaitingClose: false } : x))
    if (!msg || !msg.action) return
    try {
      await executeAIAction(msg.action, msg.params || {}, tournamentId, db, msg.command || '')
    } catch {}
  }

  // After all hooks so hook order stays stable: on Calcutta surfaces the AI is
  // suppressed entirely (the tab has its own auction AI). Must sit BEFORE the
  // collapsed-circle return below, or the ✨ bubble renders on the (!open) path.
  if (aiHidden) return null

  // ─── Floating button ────────────────────────────────────────────────────
  if (!open) {
    return createPortal((
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open Tournament AI Assistant"
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          width: PBCC_PREMIUM ? 112 : 84,
          height: PBCC_PREMIUM ? 112 : 84,
          borderRadius: '50%',
          background: PBCC_PREMIUM ? 'var(--ge-primary)' : FOURBALL_ONLY ? '#FFFFFF' : 'var(--ge-primary)',
          color: PBCC_PREMIUM ? 'var(--ge-gold)' : FOURBALL_ONLY ? 'var(--ge-primary)' : '#FFFFFF',
          border: PBCC_PREMIUM ? '2px solid var(--ge-gold)' : '2px solid var(--ge-primary)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
          cursor: 'pointer',
          zIndex: 9999,
          fontWeight: 800,
          fontSize: 13,
          letterSpacing: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          flexDirection: 'column',
          lineHeight: 1,
        }}>
        <span style={{ fontSize: PBCC_PREMIUM ? 32 : 24 }}>✨</span>
        <span style={{ fontSize: PBCC_PREMIUM ? 24 : 13, fontWeight: PBCC_PREMIUM ? 900 : 800, letterSpacing: PBCC_PREMIUM ? 2 : 1 }}>AI</span>
      </button>
    ), document.body)
  }

  // ─── Chat panel ─────────────────────────────────────────────────────────
  const panelStyle = isMobile
    ? {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        top: 'auto',
        maxHeight: '85vh',
        height: '85vh',
        background: '#FFFFFF',
        boxShadow: '0 -16px 40px rgba(0,0,0,0.35)',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
      }
    : {
        position: 'fixed',
        right: 20,
        bottom: 20,
        width: PBCC_PREMIUM ? 520 : 400,
        maxHeight: 'calc(100vh - 40px)',
        height: PBCC_PREMIUM ? 760 : 600,
        background: '#FFFFFF',
        boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
        borderRadius: 16,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
      }

  return createPortal((
    <div style={panelStyle}>
      {/* Header */}
      <div style={{
        background: 'var(--ge-primary)',
        color: '#fff',
        padding: '12px 14px',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>✨</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.1 }}>Tournament AI Assistant</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close AI Assistant"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: 22,
            cursor: 'pointer',
            lineHeight: 1,
            padding: 4,
          }}>×</button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          background: '#FFFFFF',
        }}>
        {lastTwenty.length === 0 ? (
          <EmptyState onPick={(t) => send(t)} />
        ) : (
          lastTwenty.map((m, i) => (
            <MessageBubble
              key={i}
              msg={m}
              onConfirm={() => doConfirm(messages.indexOf(m))}
              onCancel={() => doCancel(messages.indexOf(m))}
              onClose={() => doClose(messages.indexOf(m))}
              busy={busy}
            />
          ))
        )}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(input) }}
        style={{
          display: 'flex',
          gap: 8,
          padding: 10,
          borderTop: '1px solid #E5E7EB',
          background: '#FFFFFF',
          borderBottomLeftRadius: isMobile ? 0 : 16,
          borderBottomRightRadius: isMobile ? 0 : 16,
        }}>
        <input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={listening ? 'Listening…' : 'Tell me what to do…'}
          disabled={busy || listening}
          style={{
            flex: 1,
            border: '1px solid #D1D5DB',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 14,
            outline: 'none',
          }}
        />
        {voiceSupported ? (
          <button
            type="button"
            onClick={listening ? stopListening : startListening}
            disabled={busy}
            aria-label={listening ? 'Stop listening' : 'Speak your command'}
            className={listening ? 'ai-mic-listening' : ''}
            style={{
              background: listening ? '#DC2626' : 'var(--ge-primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '0 12px',
              width: 40,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            {/* Inline SVG mic so we don't depend on a new icon lib. */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
              <path d="M19 11a7 7 0 0 1-14 0" />
              <line x1="12" y1="18" x2="12" y2="22" />
              <line x1="8" y1="22" x2="16" y2="22" />
            </svg>
          </button>
        ) : null}
        <button
          type="submit"
          disabled={busy || !input.trim()}
          style={{
            background: busy || !input.trim() ? '#9CA3AF' : 'var(--ge-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '0 16px',
            fontWeight: 800,
            cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
          }}>Send</button>
      </form>

      {/* Pulse animation for the listening mic + ephemeral error toast. */}
      <style>{`
        @keyframes ai-mic-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); }
          100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
        }
        .ai-mic-listening { animation: ai-mic-pulse 1.2s ease-out infinite; }
      `}</style>
      {voiceToast ? (
        <div
          style={{
            position: 'absolute',
            bottom: 80, left: '50%', transform: 'translateX(-50%)',
            background: '#7F1D1D', color: '#fff',
            padding: '8px 14px', borderRadius: 999,
            fontSize: 12, fontWeight: 700,
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
            zIndex: 10,
            whiteSpace: 'nowrap',
          }}>
          {voiceToast}
        </div>
      ) : null}
    </div>
  ), document.body)
}

function EmptyState({ onPick }) {
  return (
    <div>
      <p style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
        I'm your tournament co-director. Tell me what you'd like to do — I'll confirm before changing anything.
      </p>
      <p style={{ fontSize: 11, fontWeight: 800, color: '#6B7280', letterSpacing: 1, marginBottom: 6, textTransform: 'uppercase' }}>
        Try one of these
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {EXAMPLES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onPick(t)}
            style={{
              textAlign: 'left',
              background: '#fff',
              border: '1px solid #D1D5DB',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 13,
              cursor: 'pointer',
              color: '#111827',
            }}>“{t}”</button>
        ))}
      </div>
    </div>
  )
}

function MessageBubble({ msg, onConfirm, onCancel, onClose, busy }) {
  if (msg.role === 'system') {
    return (
      <div style={{
        alignSelf: 'center',
        fontSize: 11,
        color: '#6B7280',
        fontStyle: 'italic',
      }}>{msg.text}</div>
    )
  }
  const isUser = msg.role === 'user'
  return (
    <div style={{
      alignSelf: isUser ? 'flex-end' : 'flex-start',
      maxWidth: '88%',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    }}>
      <div style={{
        background: isUser ? 'var(--ge-primary)' : (msg.error ? '#FEE2E2' : '#FFFFFF'),
        color: isUser ? '#fff' : (msg.error ? '#991B1B' : '#111827'),
        border: isUser ? 'none' : '1px solid #E5E7EB',
        borderRadius: 12,
        padding: '8px 12px',
        fontSize: 13,
        lineHeight: 1.45,
        whiteSpace: 'pre-wrap',
      }}>
        {msg.pending ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Spinner />
            <span>Thinking…</span>
          </span>
        ) : msg.text}
      </div>
      {msg.awaitingConfirm ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={{
              background: '#16A34A',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '6px 12px',
              fontWeight: 800,
              fontSize: 12,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}>✓ Do it</button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              background: '#DC2626',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '6px 12px',
              fontWeight: 800,
              fontSize: 12,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}>✗ Cancel</button>
        </div>
      ) : null}
      {msg.awaitingClose ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              background: 'var(--ge-primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '6px 12px',
              fontWeight: 800,
              fontSize: 12,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}>Close</button>
        </div>
      ) : null}
      {msg.executing ? (
        <div style={{ fontSize: 11, color: '#6B7280', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Spinner /> Applying…
        </div>
      ) : null}
    </div>
  )
}

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        border: '2px solid var(--ge-primary)',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'aiSpin 0.8s linear infinite',
      }}
    />
  )
}

// Keyframes for the spinner — injected once per page.
if (typeof document !== 'undefined' && !document.getElementById('ai-assistant-style')) {
  const s = document.createElement('style')
  s.id = 'ai-assistant-style'
  s.textContent = '@keyframes aiSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }'
  document.head.appendChild(s)
}
